/* Progressive Offer UI behaviour (jQuery — consistent with mrk.js / bkp.js).
 *
 * Server-rendered Liquid (snippets/offer-ui.liquid) paints the correct state on
 * load. This script keeps everything in sync with live changes:
 *   - Cart bar -> re-rendered by the theme's own "--live" cart slot, so we leave it.
 *   - Collection banner -> refreshed via the Section Rendering API (?section_id=).
 *   - Collection-tile badges -> CART-tier text, injected UNDER the price in the
 *     Boost app DOM (.boost-sd__product-price-wrapper), re-applied on re-render.
 *   - PDP badge(s) -> PRODUCT-PRICE-tier text (1 or 2 badges), updated whenever the
 *     displayed price changes (size / frame / variant).
 *
 * Cart progress is measured BEFORE discounts (original_total_price).
 * jQuery is already loaded globally; MutationObserver / DOMParser / URL are
 * browser APIs (no jQuery equivalent) and used as-is.
 */
(function ($) {
  'use strict';
  if (!$) return;

  var CART_EVENTS = 'cart:update cart:updated';
  var TILE_SELECTOR = '.boost-sd__product-price-wrapper';

  var config = readConfig();
  var state = { total: null, itemCount: null };

  /* ---- Final-state anchor: scroll only the cart's inner container --------- */
  $(document).on('click', '[data-offer-anchor]', function (event) {
    var $target = $($(this).attr('data-offer-anchor'));
    if (!$target.length) return;
    event.preventDefault();
    var target = $target.get(0);
    var container = $target.closest('.cart-drawer__items').get(0);
    var behavior = reducedMotion() ? 'auto' : 'smooth';
    if (container && typeof container.scrollBy === 'function') {
      var delta = target.getBoundingClientRect().top - container.getBoundingClientRect().top;
      container.scrollBy({ top: delta, behavior: behavior });
    } else {
      target.scrollIntoView({ behavior: behavior, block: 'start' });
    }
  });

  /* ---- Final-state (collection): open the cart drawer --------------------- */
  $(document).on('click', '[data-offer-open-cart]', function () {
    var cartButton = $('.mrk-cart-icon-button').get(0);
    if (cartButton) cartButton.click();
  });

  /* ---- Collection: "See details" reveals the bar and hides the banner ----- */
  $(document).on('click', '[data-offer-see-details]', function () {
    $(this).closest('[data-offer-collection]').addClass('is-revealed');
  });

  /* ---- Keyboard activation for non-native buttons (PDP open-cart) --------- */
  $(document).on('keydown', '[role="button"][data-offer-open-cart]', function (event) {
    if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      this.click();
    }
  });

  /* ---- React to cart changes (collection banner, tile badges, PDP) -------- */
  var onCartChange = debounce(function () {
    fetchCart(function () {
      // Guard the whole offer-UI refresh: a malformed price / missing node here must
      // not throw into the shared cart:update chain and break the add-to-cart flow.
      try {
        injectTileBadges();
        updatePdpBadge();
        updateValueProps();
        updatePdpBars();
        updateMarkdown();
        updateBadgeSaving();
        refreshSection('[data-offer-collection]', 'offer-progress-bar-collection', true);
      } catch (err) {
        console.error('[offer onCartChange]', err);
      }
    });
  }, 200);

  $(document).on(CART_EVENTS, onCartChange);

  /* ---- PDP value props also react to variant / price changes -------------- */
  (function watchPrice() {
    if (!('MutationObserver' in window)) return;
    var anchor = document.getElementById('offer-valueprops') || document.querySelector('.offer-bar--pdp') || document.querySelector('[data-offer-md]');
    if (!anchor) return;
    var scope = anchor.closest('.shopify-section') || document;
    var priceEl = scope.querySelector('.mrk-main-price') || document.querySelector('.mrk-main-price');
    if (!priceEl) return;
    // Also watch class/style changes: on the canvas/version2 template the price can be
    // resized via a class or inline style after first paint, which must re-sync the
    // discounted price's font size.
    new MutationObserver(debounce(function () { updateValueProps(); updatePdpBars(); updateMarkdown(); updateBadgeSaving(); updatePdpBadge(); }, 16))
      .observe(priceEl, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
  })();

  /* ---- Re-sync all PDP offer UI after late layout/price settle --------------
     On the canvas/version2 template the variant price (and its size) can finalise
     AFTER our first pass, which left the badge state (e.g. grey vs light-green) and
     the markdown font size stale until a full refresh. Re-run every PDP updater on
     viewport changes and once webfonts have settled. */
  function resyncPdp() {
    updateValueProps();
    updatePdpBars();
    updateMarkdown();
    updateBadgeSaving();
    updatePdpBadge();
  }
  window.addEventListener('resize', debounce(resyncPdp, 150));
  if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === 'function') {
    document.fonts.ready.then(function () { resyncPdp(); });
  }

  /* ---- React to variant changes via the theme's variant events --------------
     The theme dispatches 'variant:update' / 'variant:selected' on document after it
     updates the price in the DOM. We just re-sync, which re-reads the new VISIBLE price
     — reliable on every template (incl. canvas/version2). A next-frame re-sync covers
     any case where the price morph lands a tick after the event. */
  function onVariantEvent() {
    resyncPdp();
    if (window.requestAnimationFrame) requestAnimationFrame(resyncPdp);
  }
  document.addEventListener('variant:update', onVariantEvent);
  document.addEventListener('variant:selected', onVariantEvent);

  /* ---- Boost re-renders the grid on filter / sort / paginate / resize ----- */
  var tileObserver = null;
  if ('MutationObserver' in window && ($(TILE_SELECTOR).length || /\/(collections|search)/.test(location.pathname))) {
    tileObserver = new MutationObserver(debounce(injectTileBadges, 150));
    startTileObserver();
  }

  function startTileObserver() {
    if (tileObserver) tileObserver.observe(document.body, { childList: true, subtree: true });
  }

  /* ---- Initial state ------------------------------------------------------ */
  $(function () {
    fetchCart(function () {
      injectTileBadges();
      updatePdpBadge();
      updateValueProps();
      updatePdpBars();
      updateMarkdown();
      updateBadgeSaving();
      // Re-sync the whole PDP offer UI after first paint + a short settle, so the
      // badge state (grey/light-green/dark) and the markdown font size are correct
      // even if the variant price finalises late (canvas/version2 template).
      if (window.requestAnimationFrame) requestAnimationFrame(function () { resyncPdp(); });
      setTimeout(function () { resyncPdp(); }, 350);
    });
  });

  /* The PDP badge is left where the theme renders it — inside .offer-pdp-price-row,
     to the RIGHT of the price. (Previously it was relocated into a .mrk-offer-badges
     container, which on this theme sits BELOW the price; that relocation is removed.) */

  /* ---- Collection-tile badges (CART-driven, 2 states at tier 1) ----------- */
  function injectTileBadges() {
    if (!config || !config.plp2) return;
    // Disconnect while we mutate, and clear any existing badges first, so we can
    // never react to our own insertions or end up with duplicates on re-render/resize.
    if (tileObserver) tileObserver.disconnect();
    $('.offer-badge-collection-wrap').remove();

    // Single threshold (tier 1): below £60 shows the "spend to unlock" copy,
    // at or above £60 shows the "unlocked" copy — regardless of higher tiers.
    // On small screens use the mobile reached copy (e.g. "applied" vs "unlocked").
    var reached = state.total != null && state.total >= config.t1;
    var isMobile = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
    var reachedText = (isMobile && config.plp2m) ? config.plp2m : config.plp2[1];
    var text = reached ? reachedText : config.plp2[0];

    // Version 2 prepends a checkmark icon to the REACHED states only.
    var withTick = config.plpv2 && reached && config.tick;

    $(TILE_SELECTOR).each(function () {
      // Full-width wrapper centres the pill regardless of the tile's layout.
      var $holder = $('<div class="offer-badge-collection-wrap"><span class="offer-badge offer-badge--collection" data-offer-badge-collection></span></div>');
      var $cell = $holder.find('[data-offer-badge-collection]');
      if (withTick) {
        // Tick (trusted inline SVG from config) + admin copy as a text node (not HTML).
        $cell.addClass('offer-badge--tick').html('<span class="offer-badge__tick"><span class="offer-badge__ic offer-badge__ic--check">' + config.tick + '</span></span>');
        $cell.append(document.createTextNode(text));
      } else {
        $cell.text(text);
      }
      $(this).after($holder);
    });

    startTileObserver();
  }

  /* ---- PDP badge ---------------------------------------------------------- */
  function updatePdpBadge() {
    var badge = document.getElementById('offer-badge-pdp');
    if (!badge || !config) return;
    var mode = badge.getAttribute('data-badge-mode');
    // Static "(SAVING X)" mode is managed by updateBadgeSaving — don't touch it.
    if (mode === 'static') return;

    // States mode (option 7): all three reward badges always shown; recolour each into
    // unlocked (cart) / potential (cart+variant) / locked. Basis is always combined.
    if (mode === 'states') {
      if (state.total == null) return;
      var cartS = state.total;
      var combinedS = cartS + pdpVariantPrice(badge);
      var pillsS = badge.querySelectorAll('.offer-badge[data-pill-min]');
      for (var s = 0; s < pillsS.length; s++) {
        var minS = parseInt(pillsS[s].getAttribute('data-pill-min'), 10);
        pillsS[s].classList.remove('offer-badge--projected', 'offer-badge--locked');
        if (cartS >= minS) { /* unlocked: dark green + tick */ }
        else if (combinedS >= minS) pillsS[s].classList.add('offer-badge--projected');
        else pillsS[s].classList.add('offer-badge--locked');
      }
      return;
    }

    // Pair mode (options 8 & 9): two "{reward} at {threshold}+" badges. The discount
    // badge is always shown; the optional (free-prints) badge shows only once the basis
    // value reaches its threshold. Each badge: dark + tick when the CART unlocked it,
    // else light green + closed padlock (pending). Basis (cart / combined) only decides
    // whether the optional badge is shown.
    if (mode === 'pair') {
      if (state.total == null) return;
      var cartP = state.total;
      var isCombo = badge.getAttribute('data-badge-basis') === 'combined';
      var basisVal = cartP + (isCombo ? pdpVariantPrice(badge) : 0);
      var pillsP = badge.querySelectorAll('.offer-badge[data-pill-min]');
      for (var q = 0; q < pillsP.length; q++) {
        var pillP = pillsP[q];
        var minP = parseInt(pillP.getAttribute('data-pill-min'), 10);
        if (pillP.hasAttribute('data-pair-optional')) pillP.hidden = !(basisVal >= minP);
        pillP.classList.remove('offer-badge--pending', 'offer-badge--projected', 'offer-badge--locked');
        if (cartP >= minP) {
          /* unlocked by the cart: dark green + tick */
        } else if (isCombo) {
          // cart + variant: open-padlock light green if it would unlock, else grey locked.
          pillP.classList.add(basisVal >= minP ? 'offer-badge--projected' : 'offer-badge--locked');
        } else {
          pillP.classList.add('offer-badge--pending');
        }
      }
      return;
    }

    // Multi-badge mode: show the tier group matching the current value (cart, or
    // cart + this variant), swapping which group of pills is visible.
    if (mode === 'multi') {
      if (state.total == null) return;
      var value = state.total;
      if (badge.getAttribute('data-badge-basis') === 'combined') {
        value = state.total + pdpVariantPrice(badge);
      }
      var tier = 0;
      if (value >= config.t1) tier = 1;
      if (value >= config.t2) tier = 2;
      if (value >= config.t3) tier = 3;
      var groups = badge.querySelectorAll('.offer-badge-group');
      for (var i = 0; i < groups.length; i++) {
        groups[i].hidden = parseInt(groups[i].getAttribute('data-tier'), 10) !== tier;
      }
      // v2: colour each pill green if the cart alone unlocked it, mint if it only
      // unlocked once this product's value is added.
      if (badge.getAttribute('data-badge-v2') === 'true') {
        var cart = state.total;
        var pills = badge.querySelectorAll('.offer-badge[data-pill-min]');
        for (var p = 0; p < pills.length; p++) {
          var min = parseInt(pills[p].getAttribute('data-pill-min'), 10);
          if (cart >= min) pills[p].classList.remove('offer-badge--projected');
          else pills[p].classList.add('offer-badge--projected');
        }
      }
      // Option 6: keep the visible "Add {amount} to unlock {reward}" pill live. The
      // gap is measured from the same `value` used for tiering (cart + this variant
      // when basis is combined), so it matches the dark-green unlocked pills.
      if (badge.getAttribute('data-badge-next') === 'true') updateNextPill(badge, value);
      return;
    }

    // Single-badge (cart) mode.
    if (!config.pdp2) return;
    var reached = state.total != null && state.total >= config.t1;
    badge.textContent = reached ? config.pdp2[1] : config.pdp2[0];
  }

  /* ---- PDP progress bars: live-update fill + node states on cart change --- */
  // Mirrors the server-side fill maths in offer-ui.liquid (equal thirds,
  // interpolated within the active segment). Updates every .offer-bar--pdp.
  function pdpFill(total, t1, t2, t3) {
    var tier = 0;
    if (total >= t1) tier = 1;
    if (total >= t2) tier = 2;
    if (total >= t3) tier = 3;
    var fillH;
    if (tier === 3) fillH = 10000;
    else if (tier === 2) fillH = 6666 + (total - t2) * 3334 / (t3 - t2);
    else if (tier === 1) fillH = 3333 + (total - t1) * 3333 / (t2 - t1);
    else fillH = total * 3333 / t1;
    if (fillH > 10000) fillH = 10000;
    if (fillH < 300 && total > 0) fillH = 300;
    var next = tier === 0 ? 33.33 : (tier === 1 ? 66.66 : 100);
    return { tier: tier, fillPct: fillH / 100, nextPct: next };
  }

  // Read the PDP product price (cents) for variant/combined bars — the first VISIBLE,
  // non-zero .mrk-main-price in the bar's section (never a hidden £0.00 card).
  function pdpPriceCents(bar) {
    return readVisiblePriceCents(bar);
  }

  function updatePdpBars() {
    if (!config || state.total == null) return;
    var cart = state.total;
    var ths = [config.t1, config.t2, config.t3];
    var bars = document.querySelectorAll('.offer-bar--pdp');
    for (var b = 0; b < bars.length; b++) {
      var bar = bars[b];
      // Opt-out hook: a bar marked data-offer-static keeps its server-rendered state.
      if (bar.hasAttribute('data-offer-static')) continue;
      var basis = bar.getAttribute('data-offer-basis') || 'cart';
      var ev, pv;
      if (basis === 'variant' || basis === 'combined') {
        var price = pdpPriceCents(bar);
        if (price == null) price = 0;
        if (basis === 'variant') { ev = price; pv = price; }
        else { ev = cart; pv = cart + price; }
      } else {
        ev = cart; pv = cart;
      }
      var re = pdpFill(ev, ths[0], ths[1], ths[2]);
      var rp = pdpFill(pv, ths[0], ths[1], ths[2]);
      bar.setAttribute('data-offer-tier', re.tier);
      var track = bar.querySelector('.offer-bar__track');
      if (!track) continue;
      track.classList.toggle('is-complete', re.tier === 3);
      track.classList.toggle('is-started', ev > 0 || pv > 0);
      track.setAttribute('aria-valuenow', Math.round(re.fillPct));
      var fe = track.querySelector('.offer-bar__fill--earned');
      var fp = track.querySelector('.offer-bar__fill--proj');
      if (fe) fe.style.width = re.fillPct + '%';
      if (fp) fp.style.width = rp.fillPct + '%';
      var nodes = track.querySelectorAll('.offer-bar__node');
      for (var i = 0; i < nodes.length; i++) {
        nodes[i].classList.remove('is-earned', 'is-proj', 'is-none');
        if (ev >= ths[i]) nodes[i].classList.add('is-earned');
        else if (pv >= ths[i]) nodes[i].classList.add('is-proj');
        else nodes[i].classList.add('is-none');
      }
    }
  }

  /* ---- Offer markdown pricing (total, CTA, size + framing selectors) ------ */
  // Each markdown span carries its variant price + region discount. We show it
  // only when the offer's tier-1 discount is unlocked (by basis: cart, or
  // cart+variant). Spans next to the live .mrk-main-price (total / CTA) also
  // recompute their amount when the displayed variant price changes.
  function fmtMd(el, cents) {
    var prefix = el.getAttribute('data-md-prefix') || '';
    var compact = el.getAttribute('data-md-compact') === 'true';
    var row = el.closest('.mrk-md-row');
    var origEl = row ? row.querySelector('.mrk-md-orig') : null;
    var sample = origEl ? origEl.textContent : '';
    var symbol = (sample.match(/[^\d.,\s+-]+/) || [''])[0];
    var amount = cents / 100;
    var amountStr = compact && amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2);
    return prefix + symbol + amountStr;
  }

  function updateMarkdown() {
    if (!config || state.total == null) return;
    var basis = config.mdBasis || 'cart';
    var els = document.querySelectorAll('[data-offer-md]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var disc = parseInt(el.getAttribute('data-md-disc'), 10) || 0;
      var row = el.closest('.mrk-md-row');
      var liveOrig = row ? row.querySelector('.mrk-main-price') : null; // total / CTA track the live price
      var price;
      if (liveOrig) {
        price = parsePrice(liveOrig.textContent);
      }
      if (price == null) price = parseInt(el.getAttribute('data-md-price'), 10) || 0;
      // Already on sale (compare-at > price)? Leave the native sale alone — no markdown.
      var compareEl = row ? row.querySelector('.mrk-md-compare') : null;
      var compareCents = compareEl ? parsePrice(compareEl.textContent) : null;
      var onSale = compareCents != null && price != null && compareCents > price;
      var combinedBasis = basis === 'combined' || basis === 'combined-safe';
      var base = combinedBasis ? state.total + price : state.total;
      var active = config.mdEnabled && disc > 0 && disc < 100 && price > 0 && !onSale && base >= config.t1;
      // v3 "safer": never mark down on an empty cart, even if this variant alone would qualify.
      if (basis === 'combined-safe' && !(state.itemCount > 0)) active = false;
      el.hidden = !active;
      if (active) {
        if (liveOrig) {
          el.textContent = fmtMd(el, Math.round(price * (100 - disc) / 100));
        }
        // Match the discounted price to the original's exact rendered size.
        var origEl = row ? row.querySelector('.mrk-md-orig') : null;
        if (origEl && window.getComputedStyle) {
          var cs = window.getComputedStyle(origEl);
          el.style.fontSize = cs.fontSize;
          el.style.lineHeight = cs.lineHeight;
        }
      }
    }
  }

  /* ---- PDP badge "static + (SAVING £X)" mode ------------------------------ */
  // Same active condition + amount as the markdown, so the badge can never claim
  // a saving the price isn't showing. Tracks the selected variant's live price.
  function updateBadgeSaving() {
    var el = document.querySelector('[data-badge-saving]');
    if (!el || !config || state.total == null) return;
    var disc = parseInt(el.getAttribute('data-md-disc'), 10) || 0;
    var priceEl = document.querySelector('.offer-pdp-price-row .mrk-main-price') || document.querySelector('.mrk-main-price');
    var price = priceEl ? parsePrice(priceEl.textContent) : null;
    if (price == null) price = parseInt(el.getAttribute('data-md-price'), 10) || 0;
    var compareEl = document.querySelector('.offer-pdp-price-row .mrk-md-compare');
    var compareCents = compareEl ? parsePrice(compareEl.textContent) : null;
    var onSale = compareCents != null && price != null && compareCents > price;
    var basis = config.mdBasis || 'cart';
    var combinedBasis = basis === 'combined' || basis === 'combined-safe';
    var base = combinedBasis ? state.total + price : state.total;
    var active = config.mdEnabled && disc > 0 && disc < 100 && price > 0 && !onSale && base >= config.t1;
    if (basis === 'combined-safe' && !(state.itemCount > 0)) active = false;
    el.hidden = !active;
    if (active) {
      var saving = Math.round(price * disc / 100);
      var tpl = el.getAttribute('data-saving-tpl') || ' (SAVING {amount})';
      var symbol = ((priceEl ? priceEl.textContent : '').match(/[^\d.,\s+-]+/) || [''])[0];
      el.textContent = tpl.replace('{amount}', symbol + (saving / 100).toFixed(2));
    }
  }

  /* ---- Section Rendering API refresh (collection banner) ------------------ */
  function refreshSection(rootSelector, elementId, isCollection) {
    var $root = $(rootSelector);
    if (!$root.length) return;
    var $wrap = $root.closest('.shopify-section');
    if (!$wrap.length) return;
    var sectionId = ($wrap.attr('id') || '').replace(/^shopify-section-/, '');
    if (!sectionId) return;

    // Match the theme's own Section Rendering API usage (?section_id=).
    var url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);

    $.ajax({ url: url.toString(), dataType: 'html' }).done(function (htmlText) {
      var doc = new DOMParser().parseFromString(htmlText, 'text/html');
      var fresh = doc.getElementById(elementId);
      var live = document.getElementById(elementId);
      if (!fresh || !live) return;

      if (isCollection) {
        var $live = $(live);
        var $fresh = $(fresh);
        var freshPanel = $fresh.find('[data-offer-collection-panel]').html();
        if (freshPanel != null) $live.find('[data-offer-collection-panel]').html(freshPanel);
        // With items in the cart the bar is shown directly (banner hidden).
        if (state.itemCount > 0) {
          $live.addClass('is-revealed');
        }
      } else {
        $(live).html($(fresh).html());
      }
    });
  }

  /* ---- PDP value props (cart before discounts + current variant price) ---- */
  function updateValueProps() {
    var vp = document.getElementById('offer-valueprops');
    if (!vp || !config || state.total == null) return;
    var price = parsePrice(currentPriceText());
    if (price == null) price = 0;
    var combined = state.total + price;
    var pills = vp.querySelectorAll('.offer-vp__pill');
    for (var i = 0; i < pills.length; i++) {
      var min = parseInt(pills[i].getAttribute('data-vp-min'), 10);
      pills[i].hidden = !(combined >= min);
    }
  }

  function currentPriceText() {
    var vp = document.getElementById('offer-valueprops');
    var scope = (vp && vp.closest('.shopify-section')) || document;
    var el = scope.querySelector('.mrk-main-price') || document.querySelector('.mrk-main-price');
    return el ? el.textContent : '';
  }

  // Parse a formatted money string to integer cents, tolerant of locale separators.
  function parsePrice(text) {
    if (!text) return null;
    var cleaned = text.replace(/[^0-9.,]/g, '');
    if (!cleaned) return null;
    var frac = cleaned.match(/[.,](\d{1,2})$/);
    if (frac) {
      var intPart = cleaned.slice(0, cleaned.length - frac[0].length).replace(/[.,]/g, '');
      var cents = (parseInt(intPart || '0', 10) * 100) + parseInt((frac[1] + '0').slice(0, 2), 10);
      return isNaN(cents) ? null : cents;
    }
    var whole = parseInt(cleaned.replace(/[.,]/g, ''), 10);
    return isNaN(whole) ? null : whole * 100;
  }

  // The DISPLAYED product price (cents). The canvas/version2 template renders many
  // HIDDEN .mrk-main-price elements (£0.00 free-print cards + a per-variant price list)
  // BEFORE the visible one, so a plain querySelector returns £0. Read the first VISIBLE,
  // non-zero price instead. Scoped to the given element's product section.
  function readVisiblePriceCents(scopeEl) {
    var scope = (scopeEl && scopeEl.closest('.shopify-section')) || document;
    var els = scope.querySelectorAll('.mrk-main-price');
    var i, p;
    for (i = 0; i < els.length; i++) {
      if (els[i].offsetParent === null) continue; // skip hidden
      p = parsePrice(els[i].textContent);
      if (p != null && p > 0) return p;
    }
    // Fallback: any non-zero price anywhere in scope, then the whole document.
    for (i = 0; i < els.length; i++) { p = parsePrice(els[i].textContent); if (p != null && p > 0) return p; }
    if (scope !== document) {
      var all = document.querySelectorAll('.mrk-main-price');
      for (i = 0; i < all.length; i++) {
        if (all[i].offsetParent === null) continue;
        p = parsePrice(all[i].textContent);
        if (p != null && p > 0) return p;
      }
    }
    return null;
  }

  // This product's DISPLAYED variant price (cents) — the first VISIBLE, non-zero
  // .mrk-main-price in the badge's section (never a hidden £0.00 card / variant-list
  // entry). data-md-price is only a last resort when no visible price is found.
  function pdpVariantPrice(badge) {
    var p = readVisiblePriceCents(badge);
    if (p == null || p <= 0) p = parseInt(badge.getAttribute('data-md-price'), 10) || 0;
    return p;
  }

  // Format integer cents in the shop's presentment currency, learning the symbol,
  // its position, the decimal places and the separators from "{{ 0 | money }}"
  // (config.zeroMoney) so it stays correct across markets (£, $, €, etc.).
  function fmtSampleMoney(cents) {
    var sample = (config && config.zeroMoney) || '0.00';
    var numMatch = sample.match(/[\d.,]+/);
    var num = numMatch ? numMatch[0] : '0.00';
    var idx = sample.indexOf(num);
    var pre = sample.slice(0, idx);
    var post = sample.slice(idx + num.length);
    var decMatch = num.match(/([.,])(\d{1,2})$/);
    var decSep = decMatch ? decMatch[1] : '';
    var decN = decMatch ? decMatch[2].length : 0;
    var thouSep = decSep === ',' ? '.' : ',';
    var amt = Math.round(Math.max(0, cents)) / 100;
    var fixed = amt.toFixed(decN);
    var parts = fixed.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thouSep);
    var body = decN ? parts[0] + decSep + parts[1] : parts[0];
    return pre + body + post;
  }

  // Option 6: rewrite the visible "Add {amount} to unlock {reward}" pill so the
  // amount (gap from the cart to the next threshold) stays live as the cart changes.
  function updateNextPill(badge, value) {
    var group = badge.querySelector('.offer-badge-group:not([hidden])');
    if (!group) return;
    var pill = group.querySelector('.offer-badge--next');
    if (!pill) return;
    var slot = pill.querySelector('[data-next-text]');
    if (!slot) return;
    var base = (typeof value === 'number') ? value : (state.total || 0);
    var min = parseInt(pill.getAttribute('data-next-min'), 10) || 0;
    var gap = min - base;
    if (gap < 0) gap = 0;
    var reward = pill.getAttribute('data-next-reward') || '';
    var tmpl = (config && config.nextTmpl) || 'Add {amount} to unlock {reward}';
    slot.textContent = tmpl.replace('{amount}', fmtSampleMoney(gap)).replace('{reward}', reward);
  }

  /* ---- Helpers ------------------------------------------------------------ */
  function fetchCart(callback) {
    $.ajax({ url: '/cart.js', dataType: 'json' })
      .done(function (cart) {
        state.total = cart.original_total_price; // before discounts
        state.itemCount = cart.item_count;
        if (typeof callback === 'function') callback();
      });
  }

  function readConfig() {
    var el = document.getElementById('offer-config');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, wait);
    };
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
})(window.jQuery);
